/**
 * Superadmin Audit Log Page
 *
 * Filterable, paginated table for investigating agent actions across
 * all customer environments. Expandable rows reveal full action context.
 *
 * @module superadmin/SuperadminAuditLog
 * @see docs/action-audit-log.md
 */

import React from 'react';
import {
  Flex,
  Box,
  Heading,
  Text,
  DataTable,
  LoadingOrbit,
  Card,
  CardHeader,
  CardContent,
  Select,
  Button,
  TextField,
  TagBadge,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import type { ColumnDef, Row, StockFeatures } from '@tanstack/react-table';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { useOrganizationsQuery, useSuperadminWorkflowsQuery } from './useSuperadminData';
import {
  useAuditLogQuery,
  formatDurationMs,
  type AuditLogRecord,
  type AuditLogFilters,
} from './useAuditLogData';
import { AuditLogDetailPanel } from './AuditLogDetailPanel';
import { CheckCircle, XCircle } from '@phosphor-icons/react';

const sourceOptions = [
  { id: '', label: 'All Sources' },
  { id: 'browser', label: 'Browser' },
  { id: 'api', label: 'API' },
];

function truncateUrl(url: string | null, maxLen = 60): string {
  if (!url) return '—';
  if (url.length <= maxLen) return url;
  return url.slice(0, maxLen) + '…';
}

export function SuperadminAuditLog() {
  const search = useSearch({ from: '/superadmin/audit-log' });
  const navigate = useNavigate();

  const filters: AuditLogFilters = {
    orgId: (search as Record<string, string | undefined>).orgId,
    workflowId: (search as Record<string, string | undefined>).workflowId,
    runId: (search as Record<string, string | undefined>).runId,
    agentType: (search as Record<string, string | undefined>).agentType,
    source: (search as Record<string, string | undefined>).source,
    actionType: (search as Record<string, string | undefined>).actionType,
    from: (search as Record<string, string | undefined>).from,
    to: (search as Record<string, string | undefined>).to,
    page: (search as Record<string, string | number | undefined>).page
      ? Number((search as Record<string, string | number | undefined>).page)
      : undefined,
    pageSize: (search as Record<string, string | number | undefined>).pageSize
      ? Number((search as Record<string, string | number | undefined>).pageSize)
      : undefined,
  };

  const updateFilters = (updates: Partial<AuditLogFilters>) => {
    const merged = { ...filters, ...updates };
    // Reset to page 1 on filter change (unless page itself is being set)
    if (!('page' in updates)) {
      merged.page = 1;
    }
    navigate({
      to: '/superadmin/audit-log',
      search: Object.fromEntries(
        Object.entries(merged).filter(([, v]) => v !== null && v !== undefined && v !== '')
      ),
      replace: true,
    });
  };

  const clearFilters = () => {
    navigate({ to: '/superadmin/audit-log', search: {}, replace: true });
  };

  const organizationsQuery = useOrganizationsQuery({
    pageSize: 500,
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const workflowsQuery = useSuperadminWorkflowsQuery(
    { orgId: filters.orgId, pageSize: 100 },
    Boolean(filters.orgId)
  );
  const auditLogQuery = useAuditLogQuery(filters);

  const orgOptions = React.useMemo(() => {
    const orgs = organizationsQuery.data?.organizations ?? [];
    return [
      { id: '', label: 'All Organizations' },
      ...orgs.map((org) => ({ id: org.id, label: org.name })),
    ];
  }, [organizationsQuery.data]);

  const workflowOptions = React.useMemo(() => {
    const wfs = workflowsQuery.data?.data ?? [];
    return [
      { id: '', label: 'All Workflows' },
      ...wfs.map((wf) => ({ id: wf.id, label: wf.name })),
    ];
  }, [workflowsQuery.data]);

  const columns: ColumnDef<StockFeatures, AuditLogRecord>[] = React.useMemo(
    () => [
      {
        accessorKey: 'timestamp',
        header: 'Time',
        cell: ({ row }) => (
          <Text size="sm" className="font-mono whitespace-nowrap">
            {new Date(row.original.timestamp).toLocaleString()}
          </Text>
        ),
      },
      {
        accessorKey: 'source',
        header: 'Source',
        cell: ({ row }) => (
          <TagBadge
            name={row.original.source}
            color={badgeVariantToTagColor(
              row.original.source === 'browser' ? 'default' : 'secondary'
            )}
            hideDot
          />
        ),
      },
      {
        accessorKey: 'agentType',
        header: 'Agent',
        cell: ({ row }) => <TagBadge name={row.original.agentType} color="gray" hideDot />,
      },
      {
        accessorKey: 'actionType',
        header: 'Action',
        cell: ({ row }) => (
          <Text size="sm" weight="medium">
            {row.original.actionType}
          </Text>
        ),
      },
      {
        accessorKey: 'pageUrl',
        header: 'URL',
        cell: ({ row }) => (
          <Text
            size="sm"
            className="font-mono text-muted-foreground"
            title={row.original.pageUrl ?? undefined}
          >
            {truncateUrl(row.original.pageUrl)}
          </Text>
        ),
      },
      {
        accessorKey: 'success',
        header: 'Result',
        cell: ({ row }) => {
          if (row.original.success === null || row.original.success === undefined)
            return (
              <Text size="sm" className="text-muted-foreground">
                —
              </Text>
            );
          return row.original.success ? (
            <CheckCircle size={18} className="text-success" weight="fill" />
          ) : (
            <XCircle size={18} className="text-destructive" weight="fill" />
          );
        },
      },
      {
        accessorKey: 'durationMs',
        header: 'Duration',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground font-mono">
            {formatDurationMs(row.original.durationMs)}
          </Text>
        ),
      },
    ],
    []
  );

  const hasAnyFilter = Boolean(
    filters.orgId ||
    filters.workflowId ||
    filters.runId ||
    filters.agentType ||
    filters.source ||
    filters.actionType ||
    filters.from ||
    filters.to
  );

  return (
    <Box className="space-y-6">
      <Flex direction="column" gap="xl">
        {/* Filters */}
        <Card padding="lg">
          <CardHeader>
            <Heading level={3}>Audit Log</Heading>
            <Text size="sm" className="text-muted-foreground mt-1">
              Investigate agent actions across customer environments. Select at least one filter to
              query.
            </Text>
          </CardHeader>
          <CardContent>
            <Flex gap="md" wrap align="end">
              <Box className="min-w-[200px]">
                <Select
                  label="Organization"
                  placeholder="Filter by organization"
                  items={orgOptions}
                  selectedKey={filters.orgId ?? ''}
                  onSelectionChange={(key) => {
                    updateFilters({
                      orgId: String(key) || undefined,
                      workflowId: undefined,
                    });
                  }}
                  isDisabled={organizationsQuery.isLoading}
                />
              </Box>
              <Box className="min-w-[200px]">
                <Select
                  label="Workflow"
                  placeholder="Filter by workflow"
                  items={workflowOptions}
                  selectedKey={filters.workflowId ?? ''}
                  onSelectionChange={(key) =>
                    updateFilters({ workflowId: String(key) || undefined })
                  }
                  isDisabled={!filters.orgId || workflowsQuery.isLoading}
                />
              </Box>
              <Box className="min-w-[200px]">
                <TextField
                  label="Run ID"
                  placeholder="Paste a run UUID..."
                  value={filters.runId ?? ''}
                  onChange={(value) => updateFilters({ runId: value.trim() || undefined })}
                />
              </Box>
              <Box className="min-w-[150px]">
                <Select
                  label="Source"
                  placeholder="All sources"
                  items={sourceOptions}
                  selectedKey={filters.source ?? ''}
                  onSelectionChange={(key) => updateFilters({ source: String(key) || undefined })}
                />
              </Box>
              {hasAnyFilter && (
                <Button variant="ghost" onPress={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </Flex>
          </CardContent>
        </Card>

        {/* Results Table */}
        <Card padding="lg">
          <CardHeader>
            <Flex align="center" justify="between" wrap gap="md">
              <div>
                <Heading level={4}>
                  {auditLogQuery.data?.pagination.totalItems ?? 0} Actions
                </Heading>
              </div>
              {auditLogQuery.isLoading && <LoadingOrbit size="sm" />}
            </Flex>
          </CardHeader>
          <CardContent>
            {!hasAnyFilter ? (
              <Text className="text-muted-foreground py-8 text-center">
                Select at least one filter above to search the audit log.
              </Text>
            ) : auditLogQuery.isLoading ? (
              <Flex align="center" justify="center" className="py-10">
                <LoadingOrbit size="lg" />
              </Flex>
            ) : auditLogQuery.isError ? (
              <Text className="text-destructive">
                {auditLogQuery.error instanceof Error
                  ? auditLogQuery.error.message
                  : 'Failed to load audit log'}
              </Text>
            ) : auditLogQuery.data?.data?.length ? (
              <DataTable
                columns={columns}
                data={auditLogQuery.data.data}
                stickyHeader
                serverPagination={{
                  page: auditLogQuery.data.pagination.page,
                  pageSize: auditLogQuery.data.pagination.pageSize,
                  pageCount: auditLogQuery.data.pagination.totalPages,
                  totalItems: auditLogQuery.data.pagination.totalItems,
                  onPageChange: (p: number) => updateFilters({ page: p }),
                }}
                expandableRows={{
                  render: (row: Row<StockFeatures, AuditLogRecord>) => (
                    <AuditLogDetailPanel record={row.original} />
                  ),
                }}
              />
            ) : (
              <Text className="text-muted-foreground py-8 text-center">
                No audit log entries found. Try adjusting your filters.
              </Text>
            )}
          </CardContent>
        </Card>
      </Flex>
    </Box>
  );
}
